import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import {
  Activity,
  Store as StoreIcon,
  ExternalLink,
  Database,
  BarChart3,
  Sparkles,
  Clock,
} from 'lucide-react';
import { StoreSelector } from './halo-mmm/StoreSelector';
import { DiagnosticsSection } from './halo-mmm/DiagnosticsSection';
import { SettingsSection } from './halo-mmm/SettingsSection';
import { ComputationSection } from './halo-mmm/ComputationSection';
import { V2FeaturesSection } from './halo-mmm/V2FeaturesSection';
import { MultiSignalSection } from './halo-mmm/MultiSignalSection';
import type { HaloDiagnostics } from './halo-mmm/types';
import { daysAgo, today, safeLocaleNumber, formatDateShort, timeAgo } from './halo-mmm/types';
import type { V2Features } from '@halo/web-types';

type TabKey = 'overview' | 'features' | 'computation';

const TABS: readonly { key: TabKey; label: string; icon: React.ElementType }[] = [
  { key: 'overview', label: 'Overview', icon: BarChart3 },
  { key: 'features', label: 'Features', icon: Sparkles },
  { key: 'computation', label: 'Computation', icon: Activity },
];

interface HaloSettingsWithFeatures {
  storeId: number;
  cpmRate: number;
  v2Features: V2Features;
}

export function HaloMmmPage() {
  const [selectedStore, setSelectedStore] = useState<{ id: number; name: string } | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">HaloMMM</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Attribution modeling diagnostics, settings, and computation
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-gray-400 dark:text-gray-500">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Live
        </div>
      </div>

      {/* Store Selector */}
      <div className="mb-5">
        <StoreSelector
          selectedStoreId={selectedStore?.id ?? null}
          onSelect={(id, name) => {
            setSelectedStore({ id, name });
            setActiveTab('overview');
          }}
        />
      </div>

      {/* Store Content */}
      {selectedStore ? (
        <StoreContent
          storeId={selectedStore.id}
          storeName={selectedStore.name}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-16 text-center">
          <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
            <Activity className="w-7 h-7 text-gray-300" />
          </div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">No store selected</p>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Select a store above to view HaloMMM diagnostics and controls
          </p>
        </div>
      )}

    </div>
  );
}

// ─── Store Content with Stats + Tabs ─────────────

function StoreContent({
  storeId,
  storeName,
  activeTab,
  onTabChange,
}: {
  readonly storeId: number;
  readonly storeName: string;
  readonly activeTab: TabKey;
  readonly onTabChange: (tab: TabKey) => void;
}) {
  const startDate = daysAgo(90);
  const endDate = today();

  const { data: diagnostics } = useQuery({
    queryKey: ['admin', 'halo-diagnostics', storeId, startDate, endDate],
    queryFn: async ({ signal }) => {
      const res = await api.get<HaloDiagnostics>(
        `/api/v1/ingestion/admin/halo-diagnostics?storeId=${storeId}&startDate=${startDate}&endDate=${endDate}`,
        { signal },
      );
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed');
      return res.data;
    },
  });

  const { data: settingsData } = useQuery({
    queryKey: ['admin', 'halo-settings', storeId],
    queryFn: async ({ signal }) => {
      const res = await api.get<HaloSettingsWithFeatures>(
        `/api/v1/analytics/halo-settings?storeId=${storeId}`,
        { signal },
      );
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed');
      return res.data;
    },
  });

  const baselineRows = diagnostics?.baseline?.rowCount ?? 0;
  const attrRows = diagnostics?.attribution?.rowCount ?? 0;
  const lastComputed = diagnostics?.attribution?.maxDate ?? diagnostics?.baseline?.maxDate ?? null;

  const enabledFeatureCount = useMemo(() => {
    if (!settingsData?.v2Features) return 0;
    return Object.values(settingsData.v2Features).filter(Boolean).length;
  }, [settingsData?.v2Features]);

  return (
    <div>
      {/* Store Header Pill */}
      <div className="flex items-center justify-between mb-4 px-1">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-emerald-100 flex items-center justify-center">
            <StoreIcon className="w-3 h-3 text-emerald-700" />
          </div>
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{storeName}</span>
          <span className="text-[10px] text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded font-mono">
            #{storeId}
          </span>
        </div>
        <a
          href={`/stores/${storeId}`}
          className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        >
          View Store
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Stats Summary Bar */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        <StatCard
          icon={Database}
          iconBg="bg-blue-50 dark:bg-blue-950/40"
          iconColor="text-blue-600 dark:text-blue-400"
          label="Baseline Rows"
          value={safeLocaleNumber(baselineRows)}
        />
        <StatCard
          icon={BarChart3}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
          label="Attribution Rows"
          value={safeLocaleNumber(attrRows)}
        />
        <StatCard
          icon={Clock}
          iconBg="bg-amber-50 dark:bg-amber-950/40"
          iconColor="text-amber-600"
          label="Last Computed"
          value={lastComputed ? formatDateShort(lastComputed) : 'Never'}
          subValue={lastComputed ? timeAgo(lastComputed) : undefined}
        />
        <StatCard
          icon={Sparkles}
          iconBg="bg-purple-50 dark:bg-purple-950/40"
          iconColor="text-purple-600 dark:text-purple-400"
          label="Features Enabled"
          value={`${enabledFeatureCount} / 11`}
        />
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 mb-5 border-b border-gray-200 dark:border-gray-800">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => onTabChange(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                isActive
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-400 dark:text-gray-500 hover:text-gray-600'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <DiagnosticsSection storeId={storeId} />
            <SettingsSection storeId={storeId} />
          </div>
          <MultiSignalSection storeId={storeId} />
        </div>
      )}

      {activeTab === 'features' && (
        <V2FeaturesSection storeId={storeId} />
      )}

      {activeTab === 'computation' && (
        <ComputationSection storeId={storeId} />
      )}
    </div>
  );
}

// ─── Stat Card ───────────────────────────────────

function StatCard({
  icon: Icon,
  iconBg,
  iconColor,
  label,
  value,
  subValue,
}: {
  readonly icon: React.ElementType;
  readonly iconBg: string;
  readonly iconColor: string;
  readonly label: string;
  readonly value: string;
  readonly subValue?: string;
}) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-6 h-6 rounded-lg ${iconBg} flex items-center justify-center`}>
          <Icon className={`w-3 h-3 ${iconColor}`} />
        </div>
        <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</span>
      </div>
      <span className="text-lg font-semibold text-gray-900 dark:text-gray-100 block">{value}</span>
      {subValue && <span className="text-[10px] text-gray-400 dark:text-gray-500">{subValue}</span>}
    </div>
  );
}
